import React, { useState } from 'react';
import { ProgressBar } from '../../primitives/ProgressBar';
import { TomeButton, TomeChip, type TomeTone } from '../../primitives/tome';
import { PLAYER_UI, formatCallingName } from '../../theme';
import { journalObjectives, journalTurnIn, type JournalQuest } from './questJournalModel';
import { JournalRewards } from './JournalRewards';
import { QuestPath } from '../../Systems/NPC/codex/QuestPath';
import { QuestPreviewCard } from './QuestPreviewCard';
import { previewHover } from './previewHover';
import { questEndNpcDisplay, resolveQuestEndNpcDisplay, UNKNOWN_QUEST_END_NPC_NAME } from '../../../../shared/quests/questEndNpcDisplay';
import { JournalAtlasLink } from './JournalAtlasLink';
import { JournalReference, earlierQuestLabel, referenceQuest } from './JournalReference';

interface JournalDetailProps {
  quest: JournalQuest;
  completedIds: readonly string[];
  focused: boolean;
  /** The character's class, for a reference entry's class-specific listed rewards. */
  playerClass?: string;
  onFocus?: () => void;
  onClearFocus?: () => void;
}

const KICKER: Record<JournalQuest['state'], { label: string; tone: TomeTone }> = {
  available: { label: 'Ready to take up', tone: 'available' },
  locked: { label: 'Locked chapter', tone: 'locked' },
  completed: { label: 'Completed quest', tone: 'completed' },
  ready: { label: 'Ready to turn in', tone: 'ready' },
  active: { label: 'In progress', tone: 'active' },
};

/** What still holds a discovered chapter back. */
const LockedNote: React.FC<{ lockedBy: JournalQuest['lockedBy'] }> = ({ lockedBy }) => {
  const text = lockedBy?.notOffered ? 'No one offers this quest now'
    : lockedBy?.quests.length ? 'Complete earlier chapter quests to unlock'
      : lockedBy?.level !== undefined ? `Reach level ${lockedBy.level} to take this quest up` : null;
  return text ? <p className="quest-journal-locked-note"><span aria-hidden>🔒</span> {text}</p> : null;
};

/**
 * One quest on the reading page. Completed ledger IDs describe completion only; authored details are not
 * reward receipts. Earlier quests are real buttons: hover previews, click/Enter/tap pins a reference note.
 */
export const JournalDetail: React.FC<JournalDetailProps> = ({ quest, completedIds, focused, playerClass, onFocus, onClearFocus }) => {
  const [hoveredPrereq, setHoveredPrereq] = useState<{ quest: JournalQuest; pos: { x: number; y: number } } | null>(null);
  const [pinned, setPinned] = useState<{ owner: string; trail: string[] } | null>(null);
  const reference = quest.state === 'completed';
  const isLocked = quest.state === 'locked';
  // A discovered chapter, locked or ready to take up: no live progress, and its giver starts it.
  const isDiscovered = isLocked || quest.state === 'available';
  const giver = quest.definition?.startNpc ? resolveQuestEndNpcDisplay(quest.definition.startNpc).name : UNKNOWN_QUEST_END_NPC_NAME;
  const hasGiver = giver !== UNKNOWN_QUEST_END_NPC_NAME;
  const prerequisites = quest.definition?.requiredQuests ?? [];
  const kicker = KICKER[quest.state];
  // A pinned note belongs to the quest it was opened from; selecting another quest closes it.
  const trail = pinned && pinned.owner === quest.id ? pinned.trail : null;

  return <section className="quest-journal-detail" aria-label={`${quest.title} quest details`}>
    <div className="quest-journal-reading">
      <header>
        <TomeChip tone={kicker.tone}>{kicker.label}</TomeChip>
        <h2>{quest.title}</h2>{quest.title === 'Unknown quest' && <p className="quest-journal-unknown-id">{quest.id}</p>}
      </header>
      <p className="quest-journal-description">{quest.description}</p>
      {isDiscovered && hasGiver && quest.definition?.startNpc
        && <p className="quest-journal-giver">Given by <strong>{giver}</strong>
          <JournalAtlasLink npcId={quest.definition.startNpc} role="giver" /></p>}
      {isLocked && <LockedNote lockedBy={quest.lockedBy} />}
      {quest.path && quest.path.length > 0 && <div className="quest-journal-path"><QuestPath steps={quest.path} /></div>}
      {quest.definition && <div className="quest-journal-requirements">
        <span>Quest level {quest.definition.requiredLevel}</span>
        {quest.definition.requiredClass && <span>Calling: {formatCallingName(quest.definition.requiredClass)}</span>}
        {prerequisites.map(id => {
          const prereq = referenceQuest(id, completedIds);
          return <span key={id}>Earlier quest:{' '}
            <button type="button" className="quest-journal-prereq" aria-expanded={trail?.[0] === id}
              aria-label={earlierQuestLabel(prereq)}
              onClick={() => setPinned(trail?.length === 1 && trail[0] === id ? null : { owner: quest.id, trail: [id] })}
              {...previewHover(pos => setHoveredPrereq({ quest: prereq, pos }), () => setHoveredPrereq(null))}>
              {prereq.title}
            </button>
            <small>{prereq.state === 'completed' ? 'Completed' : 'Not completed'}</small>
          </span>;
        })}
      </div>}
      {trail && <JournalReference trail={trail} completedIds={completedIds} playerClass={playerClass}
        onFollow={id => setPinned({ owner: quest.id, trail: [...trail, id] })}
        onBack={() => setPinned({ owner: quest.id, trail: trail.slice(0, -1) })}
        onClose={() => setPinned(null)} />}
      {!reference && !isDiscovered && quest.live && <>
        <p className="quest-journal-turn-in" data-testid={`quest-log-end-npc-${quest.id}`}>
          Turn in to: <strong>{journalTurnIn(quest.live)}</strong>
          <JournalAtlasLink npcId={questEndNpcDisplay(quest.id).npcId} role="turn-in" />
        </p>
        <section className="quest-journal-objectives" aria-label="Live objectives"><h3>Objectives</h3>
          {journalObjectives(quest.live).map((objective, index) => <div className="quest-journal-objective" key={index} data-objective-met={objective.met}>
            <div><strong>{objective.label}</strong><span>{objective.progress}</span></div>
            {objective.knownRequirement && objective.knownCurrent && <ProgressBar value={objective.current} max={objective.required}
              height={5} showPercentage={false} customColor={objective.met ? PLAYER_UI.success : PLAYER_UI.brass} />}
            {objective.description && <p>{objective.description}</p>}
          </div>)}
        </section>
      </>}
      <JournalRewards questId={quest.id} live={reference || isDiscovered ? undefined : quest.live?.rewards}
        referenceOnly={reference || isDiscovered} playerClass={playerClass} />
    </div>
    {!reference && <footer className="quest-journal-detail-actions">
      <span>{isLocked ? 'Its giver offers this chapter once the note above is met.'
        : quest.state === 'available' ? `Speak with ${hasGiver ? giver : 'its giver'} to take this quest up.`
        : quest.state === 'ready' ? 'Speak with the named NPC to turn this quest in.' : 'Progress updates as you play.'}</span>
      {!isDiscovered && (focused
        ? <TomeButton tone="secondary" onClick={onClearFocus} disabled={!onClearFocus}>Clear tracker focus</TomeButton>
        : <TomeButton tone="primary" onClick={onFocus} disabled={!onFocus}>Focus in tracker</TomeButton>)}
    </footer>}
    {hoveredPrereq && <QuestPreviewCard quest={hoveredPrereq.quest} completedIds={completedIds} pointerPos={hoveredPrereq.pos} />}
  </section>;
};
